import { useAppSelector } from "../redux";

/**
 * Returns the organization the client is currently operating in (the one the
 * `X-Org-Id` header points at on every request). Also exposes two flags:
 *
 * - `isUsingNonDefaultOrg`: generic "current != user's server-side default". True
 *   for any user who has switched away from their default — no special meaning
 *   beyond "you've moved". Not a signal to show admin chrome.
 *
 * - `isAdminViewingForeignOrg`: super-admin is viewing an org they are NOT a
 *   member of. This is the genuine "admin override" state and is what should
 *   drive things like the ADMIN MODE banner / cyan chrome. A super-admin
 *   switching between orgs they're a real member of is normal multi-org
 *   operation, not admin mode.
 */
export const useEffectiveOrganization = () => {
  const user = useAppSelector((state) => state.session.user);
  const currentOrganizationId = useAppSelector((state) => state.session.currentOrganizationId);
  const currentOrganization = useAppSelector((state) => state.session.currentOrganization);

  const memberships = user?.organizations ?? [];
  const isMemberOfCurrent = !!currentOrganizationId && memberships.some((m) => m.id === currentOrganizationId);

  const isUsingNonDefaultOrg = !!currentOrganizationId && !!user?.defaultOrganizationId && currentOrganizationId !== user.defaultOrganizationId;

  const isAdminViewingForeignOrg = !!user?.isAdmin && !!currentOrganizationId && !isMemberOfCurrent;

  return {
    organization: currentOrganization,
    organizationId: currentOrganizationId,
    isUsingNonDefaultOrg,
    isAdminViewingForeignOrg,
  };
};
